import { IRynReplayEnvelope } from '@ups/xplat/web/core';
import type { IRynReplayStream } from './ryn-replay-stream';

/**
 * Devtools-panel data model for the replay player (#30095): the console
 * stream, the network waterfall, the user-interaction trail, and the timeline
 * markers derived from all three.
 *
 * Pure and DOM-free like the stream parser, and for the same reason: this is
 * where a recorded console line or request either becomes reviewable or gets
 * lost, and none of that should need a browser to test.
 *
 * Tolerant on purpose. These payloads are produced by the recorder's devtools
 * capture (`RynReplayDevtoolsCapture`, #30070) and travel as opaque JSON, so a
 * field the capture side adds later must degrade to "shown without it", never
 * to a dropped entry. A reader returns `undefined` only when the envelope has
 * nothing displayable at all.
 */

/**
 * Envelope type strings, matching the recorder's
 * `RYN_REPLAY_CONSOLE_EVENT_TYPE` / `RYN_REPLAY_NETWORK_EVENT_TYPE`.
 *
 * Declared here rather than imported because the player must be able to read
 * sessions recorded by ANY deployed recorder version, so the wire strings are
 * the contract, not a shared constant. If the recorder renamed its constant,
 * old recordings would still say 'Console' and this parser must still read
 * them.
 */
export const RYN_REPLAY_CONSOLE_EVENT_TYPE = 'Console';
export const RYN_REPLAY_NETWORK_EVENT_TYPE = 'Network';
export const RYN_REPLAY_PERFORMANCE_EVENT_TYPE = 'Performance';

export type RynReplayConsoleLevel = 'log' | 'info' | 'warn' | 'error';

const CONSOLE_LEVELS: readonly RynReplayConsoleLevel[] = [
  'log',
  'info',
  'warn',
  'error',
];

export interface IRynReplayConsoleEntry {
  /** Epoch milliseconds UTC, matching the rrweb event clock. */
  ts: number;
  level: RynReplayConsoleLevel;
  /** The console arguments, pre-rendered for display. */
  text: string;
}

export interface IRynReplayNetworkEntry {
  /** Epoch milliseconds UTC of request COMPLETION (the capture emits on settle). */
  ts: number;
  method: string;
  url: string;
  /** HTTP status. 0 means the request never got a response (network error). */
  status: number;
  durationMs: number;
  /**
   * Present only when the recording app's payload allowlist permitted body
   * capture for this URL. Absence is the NORMAL state, not damage: the
   * allowlist is fail-closed and defaults to empty.
   */
  responseBody?: string;
  /** Serialized fetch/XHR error for requests that never completed. */
  error?: string;
  /**
   * 32-hex App Insights operation_Id. Absent on sessions recorded before
   * trace stamping shipped, and on ingest POSTs which we deliberately skip.
   */
  operationId?: string;
  /** True for status 0 (never completed) and any 4xx/5xx. */
  failed: boolean;
}

export type RynReplayPerformanceMetricId = 'fp' | 'fcp' | 'fid' | 'lcp' | 'cls';

export type RynReplayPerformanceUnit = 'ms' | 'score';

const PERFORMANCE_METRIC_IDS: readonly RynReplayPerformanceMetricId[] = [
  'fp',
  'fcp',
  'fid',
  'lcp',
  'cls',
];

export interface IRynReplayPerformanceEntry {
  /** Epoch milliseconds UTC when the metric was captured. */
  ts: number;
  metric: RynReplayPerformanceMetricId;
  value: number;
  unit: RynReplayPerformanceUnit;
  pageUrl: string;
}

/**
 * Render one captured console argument the way devtools would: strings as
 * themselves, everything else as JSON. The capture side already truncated and
 * de-cycled, so this only has to be presentable, not defensive.
 */
const renderConsoleArg = (arg: unknown): string => {
  if (typeof arg === 'string') {
    return arg;
  }
  if (arg === undefined) {
    return 'undefined';
  }
  try {
    return JSON.stringify(arg);
  } catch {
    return String(arg);
  }
};

export const formatRynReplayConsoleArgs = (args: unknown[]): string =>
  args.map(renderConsoleArg).join(' ');

/**
 * Read a `Console` envelope. Level falls back to 'log' rather than dropping
 * the entry: a level this parser does not know still carried a message
 * somebody logged.
 */
export const readRynReplayConsoleEntry = (
  envelope: IRynReplayEnvelope
): IRynReplayConsoleEntry | undefined => {
  const payload = envelope.payload as
    | { level?: unknown; args?: unknown }
    | undefined;
  const args = Array.isArray(payload?.args) ? payload.args : undefined;
  if (!args || typeof envelope.ts !== 'number') {
    return undefined;
  }
  const level = CONSOLE_LEVELS.includes(payload?.level as RynReplayConsoleLevel)
    ? (payload?.level as RynReplayConsoleLevel)
    : 'log';
  return {
    ts: envelope.ts,
    level,
    text: formatRynReplayConsoleArgs(args),
  };
};

/**
 * Read a `Network` envelope. Method and URL are the two fields the acceptance
 * criteria promise even with an empty allowlist, so an envelope missing both
 * has nothing to show and is dropped.
 */
export const readRynReplayNetworkEntry = (
  envelope: IRynReplayEnvelope
): IRynReplayNetworkEntry | undefined => {
  const payload = envelope.payload as
    | {
        method?: unknown;
        url?: unknown;
        status?: unknown;
        durationMs?: unknown;
        responseBody?: unknown;
        error?: unknown;
        operationId?: unknown;
      }
    | undefined;
  const url = typeof payload?.url === 'string' ? payload.url : undefined;
  if (!url || typeof envelope.ts !== 'number') {
    return undefined;
  }
  const status = typeof payload?.status === 'number' ? payload.status : 0;
  return {
    ts: envelope.ts,
    method:
      typeof payload?.method === 'string' && payload.method.length > 0
        ? payload.method
        : 'GET',
    url,
    status,
    durationMs:
      typeof payload?.durationMs === 'number' && payload.durationMs >= 0
        ? payload.durationMs
        : 0,
    ...(typeof payload?.responseBody === 'string'
      ? { responseBody: payload.responseBody }
      : {}),
    ...(payload?.error !== undefined
      ? { error: renderConsoleArg(payload.error) }
      : {}),
    ...(typeof payload?.operationId === 'string' &&
    payload.operationId.length > 0
      ? { operationId: payload.operationId }
      : {}),
    failed: status === 0 || status >= 400,
  };
};

/**
 * Read a `Performance` envelope. Unknown metric ids are dropped because the
 * Performance tab has no row to show them on.
 */
export const readRynReplayPerformanceEntry = (
  envelope: IRynReplayEnvelope
): IRynReplayPerformanceEntry | undefined => {
  const payload = envelope.payload as
    | {
        metric?: unknown;
        value?: unknown;
        unit?: unknown;
        pageUrl?: unknown;
      }
    | undefined;
  const metric = payload?.metric;
  if (
    typeof metric !== 'string' ||
    !PERFORMANCE_METRIC_IDS.includes(metric as RynReplayPerformanceMetricId) ||
    typeof envelope.ts !== 'number' ||
    typeof payload?.value !== 'number' ||
    !Number.isFinite(payload.value)
  ) {
    return undefined;
  }
  const unit = payload?.unit === 'score' ? 'score' : 'ms';
  return {
    ts: envelope.ts,
    metric: metric as RynReplayPerformanceMetricId,
    value: payload.value,
    unit,
    pageUrl:
      typeof payload?.pageUrl === 'string' && payload.pageUrl.length > 0
        ? payload.pageUrl
        : typeof envelope.url === 'string'
          ? envelope.url
          : '',
  };
};

export const normalizeRynReplayPerformancePageUrl = (url: string): string =>
  url.replace(/\/$/, '');

export const rynReplayPerformancePageUrlsMatch = (
  left: string | undefined,
  right: string | undefined
): boolean => {
  if (!left || !right) {
    return true;
  }
  return (
    normalizeRynReplayPerformancePageUrl(left) ===
    normalizeRynReplayPerformancePageUrl(right)
  );
};

/**
 * Latest captured value per metric at or before the playhead for the page URL
 * shown in the Performance tab heading.
 */
export const resolveRynReplayPerformanceAtPlayhead = (
  entries: readonly IRynReplayPerformanceEntry[],
  absoluteTs: number,
  pageUrl?: string
): Partial<
  Record<RynReplayPerformanceMetricId, IRynReplayPerformanceEntry>
> => {
  const resolved: Partial<
    Record<RynReplayPerformanceMetricId, IRynReplayPerformanceEntry>
  > = {};
  for (const entry of entries) {
    if (entry.ts > absoluteTs) {
      continue;
    }
    if (!rynReplayPerformancePageUrlsMatch(pageUrl, entry.pageUrl)) {
      continue;
    }
    const existing = resolved[entry.metric];
    if (!existing || entry.ts >= existing.ts) {
      resolved[entry.metric] = entry;
    }
  }
  return resolved;
};

export const formatRynReplayPerformanceValue = (
  entry?: IRynReplayPerformanceEntry
): string => {
  if (!entry) {
    return '—';
  }
  if (entry.unit === 'score') {
    return entry.value.toFixed(3);
  }
  return `${Math.round(entry.value)} ms`;
};

// ---------------------------------------------------------------------------
// User interactions, read back out of the rrweb event stream.
// ---------------------------------------------------------------------------

/**
 * rrweb wire constants, matched by value rather than imported so a rrweb major
 * version cannot silently renumber what old recordings already contain. Same
 * policy as `RRWEB_FULL_SNAPSHOT_TYPE` in the stream parser. Checked against
 * rrweb 2.x: EventType.Meta = 4, EventType.IncrementalSnapshot = 3,
 * IncrementalSource.MouseInteraction = 2, IncrementalSource.Input = 5,
 * MouseInteractions.Click = 2, DblClick = 4, TouchStart = 7.
 */
const RRWEB_META_TYPE = 4;
const RRWEB_INCREMENTAL_TYPE = 3;
const RRWEB_SOURCE_MOUSE_INTERACTION = 2;
const RRWEB_SOURCE_INPUT = 5;
const RRWEB_CLICKISH_INTERACTIONS = new Set([2, 4, 7]);

export type RynReplayInteractionKind = 'click' | 'input' | 'navigation';

export interface IRynReplayInteractionMarker {
  /** Epoch milliseconds UTC, straight off the rrweb event. */
  ts: number;
  kind: RynReplayInteractionKind;
  label: string;
  /** Present on navigation markers (rrweb Meta href). */
  href?: string;
}

/** Page URL at an absolute epoch time from navigation markers (last at or before ts). */
export const resolveRynReplayHrefAtAbsoluteTs = (
  interactions: readonly IRynReplayInteractionMarker[],
  absoluteTs: number
): string | undefined => {
  let href: string | undefined;
  for (const item of interactions) {
    if (item.kind !== 'navigation' || item.ts > absoluteTs) {
      continue;
    }
    if (item.href) {
      href = item.href;
    } else {
      const prefix = 'Navigated: ';
      if (item.label.startsWith(prefix)) {
        href = item.label.slice(prefix.length).trim() || href;
      }
    }
  }
  return href;
};

/**
 * Consecutive Input events closer together than this collapse into one
 * marker. rrweb emits one Input event per keystroke, so without coalescing a
 * typed sentence becomes a solid bar of markers that hides everything else on
 * the timeline.
 */
export const RYN_REPLAY_INPUT_COALESCE_MS = 1_000;

/**
 * Lift the user-interaction trail out of the rrweb events: clicks and taps,
 * typing bursts, and navigations (rrweb Meta events, which carry the href the
 * page changed to).
 *
 * Events are treated as the opaque `unknown[]` the stream hands over, with
 * shape-checked reads, because this lib deliberately does not import rrweb's
 * types (see IRynReplayStream.events).
 */
export const extractRynReplayInteractions = (
  events: unknown[]
): IRynReplayInteractionMarker[] => {
  const markers: IRynReplayInteractionMarker[] = [];
  let lastInputTs: number | undefined;

  for (const candidate of events) {
    const event = candidate as {
      type?: unknown;
      timestamp?: unknown;
      data?: { source?: unknown; type?: unknown; href?: unknown };
    };
    if (
      typeof event?.type !== 'number' ||
      typeof event.timestamp !== 'number'
    ) {
      continue;
    }

    if (event.type === RRWEB_META_TYPE) {
      const href =
        typeof event.data?.href === 'string' ? event.data.href : undefined;
      markers.push({
        ts: event.timestamp,
        kind: 'navigation',
        label: href ? `Navigated: ${href}` : 'Navigated',
        ...(href ? { href } : {}),
      });
      continue;
    }

    if (event.type !== RRWEB_INCREMENTAL_TYPE) {
      continue;
    }

    if (
      event.data?.source === RRWEB_SOURCE_MOUSE_INTERACTION &&
      typeof event.data.type === 'number' &&
      RRWEB_CLICKISH_INTERACTIONS.has(event.data.type)
    ) {
      markers.push({ ts: event.timestamp, kind: 'click', label: 'Click' });
      continue;
    }

    if (event.data?.source === RRWEB_SOURCE_INPUT) {
      if (
        lastInputTs !== undefined &&
        event.timestamp - lastInputTs < RYN_REPLAY_INPUT_COALESCE_MS
      ) {
        lastInputTs = event.timestamp;
        continue;
      }
      lastInputTs = event.timestamp;
      markers.push({ ts: event.timestamp, kind: 'input', label: 'Typing' });
    }
  }

  return markers;
};

const GESTURE_LABELS: Record<string, string> = {
  tap: 'Tap',
  longpress: 'Long press',
  pan: 'Swipe',
};

/**
 * Timeline markers for a mobile session: one per completed gesture, and one per
 * route change.
 *
 * Only completed gestures (`tap` / `longpress` / `pan`) become markers. The
 * `down` and `move` samples that make up a gesture are there to draw the finger,
 * and marking each would bury the bar in dozens of markers per swipe.
 */
export const extractRynReplayMobileInteractions = (
  stream: Pick<IRynReplayStream, 'mobileFrames' | 'mobileTouches'>
): IRynReplayInteractionMarker[] => {
  const markers: IRynReplayInteractionMarker[] = [];

  for (const touch of stream.mobileTouches) {
    const gesture = GESTURE_LABELS[touch.kind];
    if (!gesture) {
      continue;
    }
    const targetType =
      typeof touch.target?.type === 'string' ? touch.target.type : undefined;
    markers.push({
      ts: touch.ts,
      kind: 'click',
      label: targetType ? `${gesture}: ${targetType}` : gesture,
    });
  }

  let lastUrl: string | undefined;
  for (const frame of [...stream.mobileFrames].sort((a, b) => a.ts - b.ts)) {
    if (frame.url && frame.url !== lastUrl) {
      markers.push({
        ts: frame.ts,
        kind: 'navigation',
        label: `Navigated: ${frame.url}`,
        href: frame.url,
      });
      lastUrl = frame.url;
    }
  }

  return markers.sort((a, b) => a.ts - b.ts);
};

// ---------------------------------------------------------------------------
// Timeline markers on the duration bar.
// ---------------------------------------------------------------------------

export type RynReplayMarkerCategory =
  | 'interaction'
  | 'network-error'
  | 'console-error'
  | 'backend-error';

export interface IRynReplayTimelineMarker {
  /** Playback offset in milliseconds, already clamped into [0, totalMs]. */
  offsetMs: number;
  category: RynReplayMarkerCategory;
  /** One line for the hover tooltip. */
  summary: string;
}

export interface IRynReplayTimelineMarkerInputs {
  interactions: IRynReplayInteractionMarker[];
  consoleEntries: IRynReplayConsoleEntry[];
  networkEntries: IRynReplayNetworkEntry[];
  /** Epoch ms of playback offset zero (rrweb metadata startTime). */
  baselineTs: number;
  totalMs: number;
}

const CONSOLE_SUMMARY_MAX_CHARS = 120;

/**
 * Convert an absolute event timestamp into a playback offset, clamped into the
 * recording. Clamping rather than dropping is deliberate: console and network
 * envelopes are stamped by `Date.now()` in the recorder while rrweb stamps its
 * own clock, so an entry can land a few ms before the first frame or after the
 * last one and still belong to this recording.
 */
export const toRynReplayOffsetMs = (
  ts: number,
  baselineTs: number,
  totalMs: number
): number => Math.min(Math.max(ts - baselineTs, 0), Math.max(totalMs, 0));

/**
 * Flatten the three capture streams into one ordered marker list for the
 * duration bar: every interaction, every failed request, every console error.
 * Successful requests and non-error console lines stay in the panel tabs but
 * off the bar, otherwise a chatty app would paint the bar solid.
 */
export const deriveRynReplayTimelineMarkers = (
  inputs: IRynReplayTimelineMarkerInputs
): IRynReplayTimelineMarker[] => {
  const { interactions, consoleEntries, networkEntries, baselineTs, totalMs } =
    inputs;
  const markers: IRynReplayTimelineMarker[] = [];

  for (const interaction of interactions) {
    markers.push({
      offsetMs: toRynReplayOffsetMs(interaction.ts, baselineTs, totalMs),
      category: 'interaction',
      summary: interaction.label,
    });
  }

  for (const entry of networkEntries) {
    if (!entry.failed) {
      continue;
    }
    markers.push({
      offsetMs: toRynReplayOffsetMs(entry.ts, baselineTs, totalMs),
      category: 'network-error',
      summary: `${entry.method} ${entry.url} → ${
        entry.status === 0 ? 'failed' : entry.status
      }`,
    });
  }

  for (const entry of consoleEntries) {
    if (entry.level !== 'error') {
      continue;
    }
    markers.push({
      offsetMs: toRynReplayOffsetMs(entry.ts, baselineTs, totalMs),
      category: 'console-error',
      summary:
        entry.text.length > CONSOLE_SUMMARY_MAX_CHARS
          ? `${entry.text.slice(0, CONSOLE_SUMMARY_MAX_CHARS)}…`
          : entry.text,
    });
  }

  markers.sort((a, b) => a.offsetMs - b.offsetMs);
  return markers;
};
